import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Grid,
  Container,
  Divider,
  CircularProgress,
} from '@mui/material';
import api from '../api';

const INITIAL_FORM = {
  investment_name: '',
  buy_sell: 'BUY',
  date_purchase_sold: new Date().toISOString().split('T')[0],
  investment_type: '',
  total_contract_value: '',
  commission: '',
  gst: '',
  total_proceed: '',
  net_cash_flow: '',
  currency: 'SGD',
  status: '',
  broker: '',
  quantity: '',
  price_filled: '',
};

function InvestmentLogPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    fetchRecentTransactions();
  }, []);

  const fetchRecentTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const response = await api.get('/api/investments');
      console.log('API Response:', response.data);

      if (response.data && response.data.transactions) {
        const last10 = response.data.transactions.slice(0, 10);
        console.log('Last 10 transactions:', last10);
        setRecentTransactions(last10);
      } else {
        console.log('No transactions in response');
        setRecentTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setRecentTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await api.post('/api/investments', formData);
      setMessage('success:Transaction logged successfully!');
      setFormData(INITIAL_FORM);
      fetchRecentTransactions();
      window.scrollTo(0, 0);
    } catch (error) {
      setMessage(
        'error:Error logging transaction: ' +
          (error.response?.data?.detail || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const messageType = message.startsWith('error:') ? 'error' : 'success';
  const messageText = message.replace(/^(error|success):/, '');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="lg">
          {/* Page Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                color="primary"
                gutterBottom
              >
                Log Transaction
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Record a new investment buy or sell transaction.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/investments/holdings')}
              sx={{ whiteSpace: 'nowrap' }}
            >
              View Holdings
            </Button>
          </Box>

          {/* Alert Message */}
          {message && (
            <Alert
              severity={messageType}
              onClose={() => setMessage('')}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {messageText}
            </Alert>
          )}

          {/* Form Card */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              mb: 4,
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={3} color="text.primary">
              Transaction Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box component="form" onSubmit={handleSubmit} noValidate>
              {/* Investment Name — full width */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Investment Name (Ticker)"
                    name="investment_name"
                    value={formData.investment_name}
                    onChange={handleChange}
                    placeholder="e.g., AAPL"
                    required
                    fullWidth
                    variant="outlined"
                  />
                </Grid>

                {/* Transaction Type */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel id="buy-sell-label">Transaction Type</InputLabel>
                    <Select
                      labelId="buy-sell-label"
                      id="buy_sell"
                      name="buy_sell"
                      value={formData.buy_sell}
                      label="Transaction Type"
                      onChange={handleChange}
                    >
                      <MenuItem value="BUY">BUY</MenuItem>
                      <MenuItem value="SELL">SELL</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Date"
                    name="date_purchase_sold"
                    type="date"
                    value={formData.date_purchase_sold}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Currency */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel id="currency-label">Currency</InputLabel>
                    <Select
                      labelId="currency-label"
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      label="Currency"
                      onChange={handleChange}
                    >
                      <MenuItem value="SGD">SGD</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Investment Type */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Investment Type"
                    name="investment_type"
                    value={formData.investment_type}
                    onChange={handleChange}
                    placeholder="e.g., US Stock"
                    required
                    fullWidth
                  />
                </Grid>

                {/* Broker */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Broker"
                    name="broker"
                    value={formData.broker}
                    onChange={handleChange}
                    placeholder="e.g., Interactive Brokers"
                    required
                    fullWidth
                  />
                </Grid>

                {/* Status */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    placeholder="e.g., Completed"
                    required
                    fullWidth
                  />
                </Grid>

                {/* Quantity */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Quantity"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="e.g., 10"
                    inputProps={{ step: '0.01', min: '0.01' }}
                    required
                    fullWidth
                  />
                </Grid>

                {/* Price Filled */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Price Filled"
                    name="price_filled"
                    type="number"
                    value={formData.price_filled}
                    onChange={handleChange}
                    placeholder="e.g., 150.25"
                    inputProps={{ step: '0.01', min: '0.01' }}
                    required
                    fullWidth
                  />
                </Grid>

                {/* Total Contract Value */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Total Contract Value"
                    name="total_contract_value"
                    type="number"
                    value={formData.total_contract_value}
                    onChange={handleChange}
                    placeholder="e.g., 1000.00"
                    inputProps={{ step: '0.01' }}
                    required
                    fullWidth
                  />
                </Grid>

                {/* Commission */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Commission"
                    name="commission"
                    type="number"
                    value={formData.commission}
                    onChange={handleChange}
                    placeholder="e.g., 5.00"
                    inputProps={{ step: '0.01', min: '0' }}
                    required
                    fullWidth
                  />
                </Grid>

                {/* GST */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="GST"
                    name="gst"
                    type="number"
                    value={formData.gst}
                    onChange={handleChange}
                    placeholder="e.g., 0.35"
                    inputProps={{ step: '0.01', min: '0' }}
                    required
                    fullWidth
                  />
                </Grid>

                {/* Total Proceed */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Total Proceed"
                    name="total_proceed"
                    type="number"
                    value={formData.total_proceed}
                    onChange={handleChange}
                    placeholder="e.g., 1005.35"
                    inputProps={{ step: '0.01' }}
                    required
                    fullWidth
                  />
                </Grid>

                {/* Net Cash Flow */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Net Cash Flow"
                    name="net_cash_flow"
                    type="number"
                    value={formData.net_cash_flow}
                    onChange={handleChange}
                    placeholder="e.g., -1005.35"
                    inputProps={{ step: '0.01' }}
                    required
                    fullWidth
                  />
                </Grid>
              </Grid>

              {/* Divider before action buttons */}
              <Divider sx={{ my: 4 }} />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => navigate('/investments/holdings')}
                  sx={{ minWidth: 140 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={18} color="inherit" /> : null
                  }
                  sx={{ minWidth: 180, fontWeight: 600 }}
                >
                  {loading ? 'Logging...' : 'Log Transaction'}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Recent Transactions */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={3} color="text.primary">
              Recent Transactions
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {loadingTransactions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : recentTransactions.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No transactions yet.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.95rem',
                  }}
                >
                  <Box component="thead">
                    <Box component="tr">
                      {['Date', 'Ticker', 'Type', 'Quantity', 'Price', 'Total Value', 'Currency'].map(
                        (col) => (
                          <Box
                            component="th"
                            key={col}
                            sx={{
                              px: 2,
                              py: 1.5,
                              textAlign: 'left',
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: '0.8rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                            }}
                          >
                            {col}
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {recentTransactions.map((txn, index) => (
                      <Box
                        component="tr"
                        key={index}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                          transition: 'background 0.15s',
                        }}
                      >
                        <Box
                          component="td"
                          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}
                        >
                          {txn.date_purchase_sold}
                        </Box>
                        <Box
                          component="td"
                          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600, color: 'text.primary' }}
                        >
                          {txn.investment_name}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            fontWeight: 700,
                            color: txn.buy_sell === 'BUY' ? 'success.main' : 'error.main',
                          }}
                        >
                          {txn.buy_sell}
                        </Box>
                        <Box
                          component="td"
                          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}
                        >
                          {parseFloat(txn.quantity).toFixed(2)}
                        </Box>
                        <Box
                          component="td"
                          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}
                        >
                          {parseFloat(txn.price_filled).toFixed(2)}
                        </Box>
                        <Box
                          component="td"
                          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}
                        >
                          {parseFloat(txn.total_contract_value).toFixed(2)}
                        </Box>
                        <Box
                          component="td"
                          sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}
                        >
                          {txn.currency}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
  );
}

export default InvestmentLogPage;
